import { useMutation, useQueryClient } from '@tanstack/react-query';

import { replaceWorkflow } from '@/lib/api/generated/api';
import type {
  ImportViolation,
  WorkflowReplace,
  WorkflowView,
} from '@/lib/api/generated/model';
import { invalidateWorkspace } from '@/features/workspaces';
import { useSession } from '@/lib/auth/session';

import { clearBuilderBackup } from '../builder/builderBackupStore';
import { workflowKeys } from '../queryKeys';
import {
  WorkflowGoneError,
  WorkflowRejectedError,
  WorkflowStepsInUseError,
  WorkflowVersionConflictError,
} from '../errors';

/**
 * The builder's only structural write: replace the whole definition, and choose
 * which validation tier the service holds it to.
 *
 * One mutation for both Save and Publish. They differ only in the `status` the
 * request carries, and making them two hooks would mean two places to keep the
 * error mapping right — while the errors are where all the meaning is here.
 *
 * Four refusals, four different things for the user to do, so four types
 * (research D10). Collapsing them into one message would leave the builder
 * saying "could not save" to someone whose only problem is that they need to
 * reload:
 *
 * - **409 version conflict** — someone else saved first. Reload; nothing of
 *   yours was written.
 * - **409 steps in use** — a step you removed has tasks on it. The response
 *   names them.
 * - **422 rejection** — the document is incomplete or incoherent, with every
 *   problem located.
 * - **404 gone** — the workflow was deleted elsewhere. Reloading will not help.
 */
/**
 * The two 409 bodies, as one shape.
 *
 * The generated client types this response as the shared error envelope, which
 * has no `violations` — that field only appears on the steps-in-use refusal,
 * because it is the only one with anything to point at. Narrowing here rather
 * than casting at the use site keeps the assumption in one place and visible.
 */
type Conflict409 = {
  error?: string;
  message?: string;
  violations?: ImportViolation[];
};

export function useReplaceWorkflow(workspaceId: string) {
  const queryClient = useQueryClient();
  const owner = useSession().user?.id ?? null;

  return useMutation({
    mutationFn: async ({
      workflowId,
      body,
    }: {
      workflowId: string;
      body: WorkflowReplace;
    }): Promise<WorkflowView> => {
      const response = await replaceWorkflow(workspaceId, workflowId, body);

      if (response.status === 200) return response.data;

      if (response.status === 409) {
        // Both conflicts share a status, and the envelope's `error` code is
        // what tells them apart — a steps-in-use refusal carries violations
        // naming the blocking tasks, a version conflict carries none.
        const data: Conflict409 = response.data;

        if (data.error === 'WORKFLOW_STEPS_IN_USE' && data.violations) {
          throw new WorkflowStepsInUseError(409, {
            violations: data.violations,
          });
        }
        throw new WorkflowVersionConflictError(data.message);
      }

      if (response.status === 422) {
        throw new WorkflowRejectedError(422, response.data);
      }

      if (response.status === 404) {
        throw new WorkflowGoneError();
      }

      throw new Error('The workflow could not be saved.');
    },

    // Awaited rather than fired and forgotten, so the mutation stays pending
    // until the fresh aggregate has arrived — the builder's header, the
    // workflow list and the boards list all move together instead of the
    // header updating while the rest lags a beat behind.
    onSuccess: (view) => {
      // Saved or published: this browser's backup has nothing left to add.
      clearBuilderBackup(owner, view.id);
      // A workflow just published is opened through its pending-changes door
      // from now on (spec 144). Seeded here, before the workspace re-read
      // reports the new status, so the builder has what it needs the moment
      // it asks and does not drop to a loading state under the author.
      if (view.status === 'published') {
        queryClient.setQueryData(workflowKeys.editing(workspaceId, view.id), {
          workflow: view,
          pending_changes: null,
        });
      }
      return invalidateWorkspace(queryClient, workspaceId);
    },
  });
}
